import type { AppDispatch } from '../store';
import type { ReduxAction, ToastItem, ToastState } from '../types/common';

/**
 * Toast queue.
 *
 * This used to hold a single toast dismissed by a module-level timeout, so any
 * second `notify()` within the window silently destroyed the first — and that
 * timeout also raced the Snackbar's own `autoHideDuration`, leaving two owners
 * of one lifetime. Both problems matter more now that a single action can earn
 * points, send an award and unlock an achievement at once.
 *
 * Toasts are queued and shown one at a time; the component owns the timer.
 */

const DEFAULT_DURATION_MS = 5000;
const ERROR_DURATION_MS = 15000;

let nextId = 0;

const initialState: ToastState = { queue: [] };

const notificationReducer = (
  state: ToastState = initialState,
  action: ReduxAction<unknown>
): ToastState => {
  switch (action.type) {
    case 'ENQUEUE_NOTIFICATION': {
      const item = action.payload as ToastItem;
      // Collapse a repeat of something already queued — a burst of identical
      // messages reads as "×3" instead of blocking the queue behind duplicates.
      const existing = state.queue.findIndex(
        (t) => t.message === item.message && t.severity === item.severity
      );
      if (existing >= 0) {
        const queue = [...state.queue];
        queue[existing] = { ...queue[existing], count: queue[existing].count + 1 };
        return { queue };
      }
      return { queue: [...state.queue, item] };
    }
    case 'DISMISS_NOTIFICATION':
      return { queue: state.queue.filter((t) => t.id !== (action.payload as number)) };
    case 'CLEAR_NOTIFICATIONS':
      return { queue: [] };
    default:
      return state;
  }
};

/**
 * Queue a toast. The `(message, severity)` signature is unchanged so existing
 * call sites keep working; pass `durationMs` to override how long it shows.
 */
export const notify = (
  message: string,
  severity: string,
  options: { durationMs?: number } = {}
) => {
  return (dispatch: AppDispatch) => {
    nextId += 1;
    const item: ToastItem = {
      id: nextId,
      message,
      severity,
      duration:
        options.durationMs ??
        (severity === 'error' ? ERROR_DURATION_MS : DEFAULT_DURATION_MS),
      count: 1,
    };

    dispatch({ type: 'ENQUEUE_NOTIFICATION', payload: item });
  };
};

/** Dismiss one toast (the component does this when its timer expires). */
export const dismissNotif = (id: number) => {
  return (dispatch: AppDispatch) => {
    dispatch({ type: 'DISMISS_NOTIFICATION', payload: id });
  };
};

/** Drop everything queued — e.g. on logout. */
export const clearNotif = () => {
  return (dispatch: AppDispatch) => {
    dispatch({ type: 'CLEAR_NOTIFICATIONS' });
  };
};

export default notificationReducer;
